import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Link2 } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Proposta in blocco della fattura per più DDT non collegati, da confermare
export const BulkCollegaDdtDialog = ({ ids, onClose, onDone }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = (ids || []).length > 0;

  useEffect(() => {
    if (!open) { setRows(null); return; }
    (async () => {
      const res = [];
      for (let k = 0; k < ids.length; k += 6) {
        res.push(...await Promise.all(ids.slice(k, k + 6).map((id) => api.get(`/ddt/${id}/proposte-fatture`).then((r) => r.data).catch(() => null))));
      }
      setRows(res.filter(Boolean).map((d) => ({ ...d, sel: d.consigliata || "", on: !!d.consigliata })));
    })();
    /* eslint-disable-next-line */
  }, [open]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const scelti = (rows || []).filter((r) => r.on && r.sel);
  const sforo = (() => {
    const per = {};
    scelti.forEach((r) => { per[r.sel] = (per[r.sel] || 0) + Number(r.ddt.imponibile || 0); });
    return Object.entries(per).map(([fid, s]) => {
      const p = scelti.find((r) => r.sel === fid).proposte.find((x) => x.id === fid);
      return p && p.imponibile && s + p.ddt_somma > p.imponibile + 1 ? p.numero : null;
    }).filter(Boolean);
  })();

  const conferma = async () => {
    setBusy(true);
    let ok = 0;
    for (const r of scelti) {
      try { await api.post(`/fatture/${r.sel}/link-ddt`, { ddt_id: r.ddt.id }); ok += 1; } catch { /* continua */ }
    }
    setBusy(false);
    toast.success(`${ok} DDT collegati${scelti.length - ok ? ` · ${scelti.length - ok} non riusciti` : ""}`);
    onDone?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="bulklink-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Collega i DDT alle fatture</DialogTitle>
          <DialogDescription>Per ogni DDT ti propongo la fattura pi&ugrave; probabile. Controlla, cambia se serve e conferma in blocco.</DialogDescription>
        </DialogHeader>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="divide-y border rounded-md">
            {rows.map((r, i) => <Riga key={r.ddt.id} r={r} i={i} setRow={setRow} />)}
          </div>
        )}
        {sforo.length > 0 && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="bulklink-sforo">Attenzione: i DDT scelti superano l'imponibile della fattura n. {sforo.join(", ")}.</div>}
        <div className="flex flex-wrap justify-between items-center gap-2 pt-2">
          <span className="text-xs text-muted-foreground" data-testid="bulklink-count">{scelti.length} da collegare su {(rows || []).length}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Annulla</Button>
            <Button onClick={conferma} disabled={busy || !scelti.length} data-testid="bulklink-conferma">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Collega {scelti.length} DDT
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Riga = ({ r, i, setRow }) => (
  <div className="p-3 text-sm space-y-2 min-w-0" data-testid={`bulklink-row-${r.ddt.id}`}>
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" className="mt-1" checked={r.on} onChange={(e) => setRow(i, { on: e.target.checked })} data-testid={`bulklink-check-${r.ddt.id}`} />
      <div className="min-w-0">
        <b>DDT n. {r.ddt.numero || "s.n."}</b> &middot; {r.ddt.data} &middot; {fmt(r.ddt.imponibile)} &middot; <span className="text-muted-foreground">{r.ddt.fornitore || "fornitore non indicato"}</span>
        {!r.consigliata && <div className="text-[11px] text-amber-700">Nessuna fattura sicura: scegline una</div>}
      </div>
    </label>
    <select className="w-full max-w-full h-9 rounded-md border border-input bg-background px-2 text-xs" value={r.sel}
      onChange={(e) => setRow(i, { sel: e.target.value, on: !!e.target.value })} data-testid={`bulklink-select-${r.ddt.id}`}>
      <option value="">&mdash; non collegare &mdash;</option>
      {r.proposte.map((p) => (
        <option key={p.id} value={p.id}>
          n. {p.numero} &middot; {p.data} &middot; {p.controparte} &middot; imp. {fmt(p.imponibile)} (DDT {fmt(p.ddt_somma)}) &middot; {Math.round(p.punteggio * 100)}%
        </option>
      ))}
    </select>
  </div>
);
